import { getProfilePath } from '@/common'
import { CopyNotify, copyText } from '@/components/copy'
import { CopyIcon } from '@/components/Icon'
import Data from '@/data'
import { InventorySlotType } from '@/data/enums'
import Trans from '@/data/trans'
import { Range } from '@/data/types'
import { itemGetMods, itemMaxStats } from '@/logic/item'
import { statListFromModList } from '@/logic/mods'
import { selectItem, stateGetItem } from '@/logic/stateReducers'
import Store, { defaultPlanner, StoreState } from '@/store'
import { markupString, stripMarkup } from '@/tooltip/common'
import { CallbackWrap } from '@/types/common'
import { Profile, StatPriority } from '@/types/profile'
import Select, { underlinerFunc } from '@/ui/select'
import { faList, faTrashAlt } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd'
import { Button, Icon, Modal, OverlayContext, useConfirm } from '@maxroll-media-group/common-libs'
import classNames from 'classnames'
import _ from 'lodash'
import React, { useCallback, useEffect, useState } from 'react'
import { useEditPlanner } from '../utils/edit'
import { WidgetButton } from '../utils/widget/widget'
import styles from './priority.module.scss'

type PriorityMap = {
  [key in InventorySlotType]?: StatPriority[]
}

const defaultPriority: PriorityMap = {}

export const prioritySlots: Record<string, string> = {
  Amulet: 'Amulet',
  Belt: 'Belt',
  BodyArmour: 'Body Armour',
  Boots: 'Boots',
  Gloves: 'Gloves',
  Helm: 'Helm',
  Ring: 'Ring',
  Ring2: 'Ring 2',
  Flask1: 'Flask 1',
  Flask2: 'Flask 2',
  Flask3: 'Flask 3',
  Flask4: 'Flask 4',
  Flask5: 'Flask 5',
  Weapon: 'Weapon',
  Offhand: 'Offhand',
  Weapon2: 'Weapon 2',
  Offhand2: 'Offhand 2'
}

function usePriority() {
  const priority = Store.usePlanner(
    (planner) => planner.equipment.variants[planner.equipment.active]?.priority ?? defaultPriority
  )
  const dispatch = Store.useDispatch()
  const setPriority = React.useCallback(
    (value: CallbackWrap<PriorityMap>) =>
      dispatch.setPlanner((planner) => {
        const prev = planner.equipment.variants[planner.equipment.active]?.priority ?? defaultPriority
        if (typeof value === 'function') value = value(prev)
        if (value === prev) return planner
        planner = { ...planner }
        planner.equipment = { ...planner.equipment }
        planner.equipment.variants = planner.equipment.variants.slice()
        planner.equipment.variants[planner.equipment.active] = {
          ...planner.equipment.variants[planner.equipment.active],
          priority: value
        }
        return planner
      }),
    [dispatch]
  )
  return [priority, setPriority] as const
}

type StatOption = {
  id: string
  name: string
  text: string
}

const renderValue = (opt: StatOption | undefined, search?: RegExp) => {
  return opt?.text ? <span>{markupString(opt.text, (text) => underlinerFunc(text, search))}</span> : 'Select option...'
}

export const priorityExtraOptions: Record<string, { name: string }> = {
  // 'additional-socket': { name: '[Corrupted|Corruption]: Additional Socket' },
  'any-resistances': { name: '+% to Fire/Cold/Lightning Resistances' },
  'any-attribute': { name: '+ to Str/Dex/Int' }
}

export function getStatPriorityText(id: string, stats: string[]) {
  if (id in priorityExtraOptions) return priorityExtraOptions[id].name

  const mask = Trans.global.maskFromStatList({ [id]: 1 })
  const key = Object.keys(mask)[0]
  return key ? stats[Number(key)] : undefined
}

function StatSelector({
  stats,
  value,
  onChange
}: {
  stats: string[]
  value: string | undefined
  onChange: (value: string | undefined) => void
}) {
  const options = React.useCallback(() => {
    const options: StatOption[] = Object.entries(priorityExtraOptions).map(([id, value]) => ({
      id,
      name: stripMarkup(value.name),
      text: value.name
    }))

    stats.forEach((text, index) => {
      const line = Trans.global[index]
      if (line) {
        options.push({
          id: line.ids[0],
          name: stripMarkup(text),
          text
        })
      }
    })
    return options.filter((a) => a.text).sort((a, b) => a.name.localeCompare(b.name))
  }, [stats])
  const onSelect = React.useCallback((opt?: StatOption) => onChange(opt?.id), [onChange])
  let display: StatOption | undefined

  if (value && value in priorityExtraOptions) {
    display = { id: value, name: '', text: priorityExtraOptions[value].name }
  } else if (value) {
    const mask = Trans.global.maskFromStatList({ [value]: 1 })
    const key = Object.keys(mask)[0]
    if (key) {
      display = {
        id: value,
        name: '',
        text: stats[Number(key)]
      }
    }
  }

  const [wrapperNode, setWrapperNode] = useState<any>(undefined)
  useEffect(() => {
    setWrapperNode(document.querySelector('.poe-planner-modal.poe-PlannerItemPriority .poe-planner-modal-content'))
  }, [])

  return wrapperNode ? (
    <Select
      className={styles.PriorityModal__select}
      value={display}
      options={options}
      onChange={onSelect}
      allowEmpty
      virtualScroll
      searchLimit={10}
      searchMiddle
      renderOption={renderValue}
      renderValue={renderValue}
      placeholder={<span>Select option...</span>}
      portalTo={wrapperNode}
    />
  ) : null
}

function StatView({ stats, value }: { stats: string[]; value: string }) {
  const text = getStatPriorityText(value, stats)
  return text ? <span>{markupString(text)}</span> : null
}

export function useSlotStats(slot: InventorySlotType, variantIndex?: number) {
  const item = Store.useSelector((state) => {
    if (!state.planner) return
    const variant = state.planner.equipment.variants[variantIndex ?? state.planner.equipment.active]
    if (!variant) return
    return stateGetItem(state, variant.items[slot])
  })

  const stats = React.useMemo(() => {
    let stats: Record<string, Range | number> = {}
    if (item && item.unique) {
      stats = { ...statListFromModList(itemGetMods(item).explicit ?? {}) }
    } else if (item) {
      stats = itemMaxStats([Data.items[item.base]])
    } else {
      const items = Object.values(Data.items).filter((item) => {
        return Data.item_classes[item.item_class]?.slots?.includes(slot)
      })
      stats = itemMaxStats(items)
    }
    return Trans.global.formatStats(stats, true)
  }, [slot, item])

  return { item, stats }
}

function PrioritySection({ slot }: { slot: InventorySlotType }) {
  const [priority, setPriority] = usePriority()
  const { stats } = useSlotStats(slot)

  const onSelect = (index: number, id: string | undefined) =>
    setPriority((priority) => {
      const stats = priority[slot]?.slice() ?? []
      if (id) {
        stats[index] = {
          id,
          rank: stats[index]?.rank ?? 0
        }
      } else {
        stats.splice(index, 1)
      }
      return { ...priority, [slot]: stats }
    })

  const onDragEnd = React.useCallback(
    ({ destination, source }: DropResult) => {
      if (!destination || source.index === destination.index) return
      setPriority((priority) => {
        priority = { ...priority }
        priority[slot] = priority[slot]?.slice() ?? []
        const src = priority[slot]!.splice(source.index, 1)
        priority[slot]!.splice(destination.index, 0, ...src)
        return priority
      })
    },
    [setPriority, slot]
  )

  return (
    <div className={styles.PriorityModal__section}>
      <span className={styles.PriorityModal__sectionHeader}>
        <span className={styles.PriorityModal__sectionTitle}>{prioritySlots[slot]}</span>
        <Button
          size="sm"
          className={styles.PriorityModal__sectionDelete}
          onClick={() =>
            setPriority((priority) => {
              priority = { ...priority }
              delete priority[slot]
              return priority
            })
          }>
          <FontAwesomeIcon className={styles.PriorityModal__sectionDeleteIcon} icon={faTrashAlt} />
          Delete
        </Button>
      </span>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId={`priority-list-${slot}`}>
          {(provided) => (
            <ol {...provided.droppableProps} ref={provided.innerRef}>
              {priority[slot]?.map(({ id, rank }, index) => (
                <Draggable key={index} draggableId={id} index={index}>
                  {(provided) => (
                    <li
                      className={classNames(rank && styles['PriorityModal__list--active'])}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      ref={provided.innerRef}>
                      <div>
                        <Icon
                          className={styles.PriorityModal__dragIcon}
                          icon="icons/drag-2.svg"
                          width={11}
                          height={15}
                        />
                      </div>

                      <div
                        className={styles.PriorityModal__listNumber}
                        onClick={() =>
                          setPriority((priority) => {
                            if (!priority[slot]?.[index]) return priority
                            priority = { ...priority }
                            priority[slot] = priority[slot]!.slice()
                            priority[slot]![index] = {
                              ...priority[slot]![index],
                              rank: priority[slot]![index].rank ? 0 : 1
                            }
                            return priority
                          })
                        }>
                        <span>{index + 1}</span>
                      </div>

                      <StatSelector stats={stats} value={id} onChange={(id) => onSelect(index, id)} />
                    </li>
                  )}
                </Draggable>
              ))}
              <Draggable draggableId={'empty'} index={priority[slot]?.length ?? 0} isDragDisabled>
                {(provided) => (
                  <li {...provided.dragHandleProps} {...provided.draggableProps} ref={provided.innerRef}>
                    <StatSelector
                      stats={stats}
                      value={undefined}
                      onChange={(id) => onSelect(priority[slot]?.length ?? 0, id)}
                    />
                  </li>
                )}
              </Draggable>
              {provided.placeholder}
            </ol>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  )
}

function generatePriorityFromPlanner(planner: Profile, items: StoreState['items']) {
  const priority: {
    [key in InventorySlotType]?: StatPriority[]
  } = {}

  const variant = planner.equipment.variants[planner.equipment.active]
  for (const [slotString, id] of Object.entries(variant.items)) {
    const slot = slotString as InventorySlotType

    const item = selectItem(items, id)
    const stats = _.uniq(
      Object.keys({
        ...item?.stats?.explicit,
        ...item?.stats?.enchant
        // ...item?.stats?.unique
      })
    )

    if (stats.length !== 0) {
      priority[slot] = stats.map((value) => ({ id: value, rank: 0 }))
    }
  }

  return priority
}

function PrioritySectionView({ slot }: { slot: InventorySlotType }) {
  const [priority] = usePriority()
  const { stats } = useSlotStats(slot)

  const data = priority[slot]

  return data?.length ? (
    <div className={classNames(styles.PriorityModal__section, styles.PriorityModal__sectionView)}>
      <span className={styles.PriorityModal__sectionTitle}>{prioritySlots[slot]}</span>
      <ol>
        {data?.map(({ id, rank }, index) => (
          <li key={index} className={classNames(rank > 0 && styles['PriorityModal__list--active'])}>
            <div className={styles.PriorityModal__listNumber}>
              <span>{index + 1}</span>
            </div>

            <StatView stats={stats} value={id} />
          </li>
        ))}
      </ol>
    </div>
  ) : null
}

function ItemPriorityDialog({ onClose, onCopy }: { onClose: () => void; onCopy: (event: React.MouseEvent) => void }) {
  const { getConfirmation, ConfirmationModal } = useConfirm()
  const [priority, setPriority] = usePriority()
  const profileId = Store.useSelector((store) => store.imported?.id)

  const [wrapperNode, setWrapperNode] = useState<any>(undefined)
  useEffect(() => {
    setWrapperNode(document.querySelector('.poe-planner-modal.poe-PlannerItemPriority .poe-planner-modal-content'))
  }, [])

  const dispatch = Store.useDispatch()
  const onGenerateFromEquipmentClick = useCallback(() => {
    const generate = () => {
      dispatch.setState((state) => {
        let planner = state.planner ?? defaultPlanner()
        const prev = planner.equipment.variants[planner.equipment.active]?.priority ?? defaultPriority

        const newPriority = generatePriorityFromPlanner(planner, state.items)

        if (newPriority === prev) return state

        planner = { ...planner }
        planner.equipment = { ...planner.equipment }
        planner.equipment.variants = planner.equipment.variants.slice()
        planner.equipment.variants[planner.equipment.active] = {
          ...planner.equipment.variants[planner.equipment.active],
          priority: newPriority
        }
        return { ...state, planner }
      })
    }

    getConfirmation({
      content: (
        <span style={{ fontSize: '18px' }}>
          This will remove existing priorities. <br />
          Are you sure you want to continue?
        </span>
      )
    }).then((confirm) => {
      if (confirm) {
        generate()
      }
    })
  }, [dispatch, getConfirmation])

  return (
    <Modal
      onClose={onClose}
      className={classNames(styles.PriorityModal, 'poe-planner-modal', 'poe-PlannerItemPriority')}
      innerClassName={classNames(styles.PriorityModal__contentWrapper, 'poe-modal-content-wrapper')}>
      <div
        className={classNames(styles.PriorityModal__modalContent, 'poe-planner-modal-content', 'poeplanner-wrapper')}>
        <div className={styles.PriorityModal__titleWrapper}>
          <h3>Stat Priorities</h3>
          {/* <Button onClick={onGenerateFromEquipmentClick}>Generate From Equipment</Button> */}
          <WidgetButton onClick={onCopy}>
            <CopyIcon /> <span>Copy</span>
          </WidgetButton>
        </div>

        <div className={styles.PriorityModal__body}>
          {Object.keys(priority).map((slot) => (
            <PrioritySection key={slot} slot={slot as InventorySlotType} />
          ))}
          <Select
            className={styles.PriorityModal__select}
            portalTo={wrapperNode}
            options={Object.entries(prioritySlots)
              .filter(([id]) => !priority[id as InventorySlotType])
              .map(([id, name]) => ({ id, name }))}
            value={undefined}
            placeholder="Add Slot..."
            onChange={(opt) =>
              opt &&
              setPriority((priority) => ({
                ...priority,
                [opt.id as InventorySlotType]: priority[opt.id as InventorySlotType] ?? []
              }))
            }
          />
        </div>

        <div className={styles.PriorityModal__actions}>
          <Button buttonStyle="primary" onClick={onClose}>
            Confirm
          </Button>
        </div>

        <ConfirmationModal />
      </div>
    </Modal>
  )
}

function ItemPriorityViewDialog({ onClose }: { onClose: () => void }) {
  const [priority] = usePriority()

  return (
    <Modal
      onClose={onClose}
      className={classNames(styles.PriorityModal, 'poe-planner-modal', 'poe-PlannerItemPriority')}
      innerClassName={classNames(styles.PriorityModal__contentWrapper, 'poe-modal-content-wrapper')}>
      <div
        className={classNames(styles.PriorityModal__modalContent, 'poe-planner-modal-content', 'poeplanner-wrapper')}>
        <h3>Stat Priorities</h3>

        <div className={styles.PriorityModal__body}>
          {Object.keys(priority).map((slot) => (
            <PrioritySectionView key={slot} slot={slot as InventorySlotType} />
          ))}
        </div>

        <div className={styles.PriorityModal__actions}>
          <Button buttonStyle="primary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export function PlannerStatPriority() {
  const [open, setOpen] = React.useState(false)
  const variantIndex = Store.usePlanner((planner) => planner.equipment.active)
  const variant = Store.usePlanner((planner) => planner.equipment.variants[planner.equipment.active])
  const isEditing = useEditPlanner()
  const priority = variant?.priority
  const profileId = Store.useSelector((store) => store.imported?.id)
  const overlays = React.useContext(OverlayContext)

  const onCopy = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault()
      event.stopPropagation()
      if (!profileId) return
      const url = new URL(getProfilePath(profileId), window.location.href)
      url.hash = `#planner=${variantIndex}&statPriority`
      copyText(url.href)
      overlays.async(<CopyNotify pos={{ left: event.clientX, top: event.clientY }} />)
    },
    [overlays, profileId, variantIndex]
  )

  if (!isEditing && !(priority && Object.values(priority).some(Boolean))) return null

  let variantName = variant?.name ? variant?.name : `Set ${variantIndex + 1}`
  variantName = variantName.length > 25 ? `${variantName.slice(0, 25)}...` : variantName

  return (
    <>
      <div className={styles.Priority__button} onClick={() => setOpen(true)}>
        <FontAwesomeIcon icon={faList} /> {variantName} Stat Priority
      </div>
      {open &&
        (isEditing ? (
          <ItemPriorityDialog onClose={() => setOpen(false)} onCopy={onCopy} />
        ) : (
          <ItemPriorityViewDialog onClose={() => setOpen(false)} />
        ))}
    </>
  )
}
